import React, {cloneElement, Fragment, useState} from 'react';

import {WithRouterProps} from 'react-router';

import {UserAvatar} from 'app/components/general/avatars';
import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {useCurrentUserState} from 'app/current-user/api';
import {StyledMainPageHeaderDiv} from 'app/general/bootstrap/utils';
import {DetailLoadingPage} from 'app/general/loading/DetailLoadingPage';
import {SPPage, StyledTitleH1} from 'app/general/structures/SPPage';
import {CheckboxButton} from 'app/general/widgets/CheckboxButton';
import {GeneralEditField} from 'app/general/widgets/GeneralEditField';
import {ModernLinkTabs} from 'app/general/widgets/ModernTabs';
import {useAccountSettingsState} from 'app/settings/api';
import {getUserDetailPageURL, USERS_HOME} from 'app/urls';
import {
  useResendUserInvitation,
  useUpdateUser,
  useUserDetails,
} from 'app/user/api';
import {gt} from 'app/utils/i18n';

import {UserPageActionButtons} from './UserPageActionButtons';
import {UserRoleSwitchButton} from './UserRoleSwitchButton';
import {UserStatusesAlerts} from './UserStatusesAlerts';

interface Props extends WithRouterProps<{userid: string}> {
  children: JSX.Element;
}

export const UserDetailPage = function UserDetailPage({
  params,
  router,
  children,
}: Props) {
  const {id: currentUserId} = useCurrentUserState().data!;
  const {is_saml} = useAccountSettingsState().data!;

  const {data: user, refetch} = useUserDetails({id: params.userid});
  const [updateUser] = useUpdateUser();
  const [resendUserInvitation] = useResendUserInvitation();

  const [childrenKey, setChildrenKey] = useState(1);

  // a big hack to force children component to remount and refetch data
  const refresh = () => {
    setChildrenKey(childrenKey + 1);
    refetch();
  };

  const tabs = React.useMemo(
    () => [
      {
        label: gt('Invitations'),
        slug: 'invites',
      },
      {
        label: gt('Teams'),
        slug: 'teams',
      },
      {
        label: gt('Activity'),
        slug: 'activity',
      },
      {
        label: gt('Procedures'),
        slug: 'procedures',
      },
      {
        label: gt('Processes'),
        slug: 'processes',
      },
      {
        label: gt('Policies'),
        slug: 'policies',
      },
      {
        label: gt('Sign offs'),
        slug: 'signoffs',
      },
    ],
    []
  );

  if (user == null) {
    return <DetailLoadingPage />;
  }

  return (
    <SPPage
      pageTitle={user.name}
      breadcrumbProps={{
        docName: user.name,
      }}
      actionArea={({isSuperManager}) =>
        isSuperManager ? (
          <UserPageActionButtons
            user={user}
            onRemove={() => router.push(USERS_HOME)}
            onUpdate={refresh}
          />
        ) : null
      }
    >
      {({isSuperManager}) => (
        <Fragment>
          <StyledMainPageHeaderDiv>
            <div className="row my-3">
              <div className="col-auto pr-0">
                <div className="mt-3">
                  <UserAvatar user={user} />
                </div>
              </div>
              <div className="col">
                <div className="mb-2">
                  <StyledTitleH1 className="mb-1">
                    <GeneralEditField
                      fieldValue={user.id ? user.name : ''}
                      canEdit={
                        (isSuperManager && user.can_edit) ||
                        user.id === currentUserId
                      }
                      updateField={(name) =>
                        updateUser({id: user.id, name}).unwrap()
                      }
                      editTitle={gt('Edit name')}
                      fieldName={'name'}
                      inputClass="form-control-lg"
                      editPlaceholder={gt('Full name')}
                    />
                  </StyledTitleH1>
                  {user.emailaddresses.map((address) => (
                    <GeneralEditField
                      key={address.id}
                      fieldValue={address.email}
                      canEdit={isSuperManager || user.id === currentUserId}
                      updateField={(email) =>
                        updateUser({id: user.id, email}).unwrap()
                      }
                      editTitle={gt('Edit email')}
                      fieldName={'email'}
                      editPlaceholder={gt('email@example.com')}
                      badges={
                        !address.verified ? (
                          <span className="badge badge-warning">
                            {gt('not verified')}
                          </span>
                        ) : null
                      }
                    />
                  ))}
                  {user.emailaddresses.length > 1 ? (
                    <small>
                      {gt(
                        '*Old email addresses are removed once one is verified by this team member.'
                      )}
                    </small>
                  ) : null}
                </div>
              </div>
              {isSuperManager && user.is_guest_until == null ? (
                <div className="col-auto d-flex flex-column justify-content-start align-items-end">
                  <div className="mt-3">
                    <UserRoleSwitchButton user={user} />
                  </div>
                  <LazyTooltip
                    tooltip={gt(
                      'Member is responsible for billing of this account'
                    )}
                  >
                    <label className="small mt-3">
                      <CheckboxButton
                        isChecked={user.is_billing_admin}
                        onClick={() =>
                          updateUser({
                            id: user.id,
                            is_billing_admin: !user.is_billing_admin,
                          }).unwrap()
                        }
                        useRealCheckbox={true}
                      />{' '}
                      {gt('Billing Administrator')}
                    </label>
                  </LazyTooltip>
                </div>
              ) : null}
            </div>
            <UserStatusesAlerts
              user={user}
              isSaml={is_saml}
              isSuperManager={isSuperManager}
              refreshData={refresh}
              actions={{
                resendInvitation: () =>
                  resendUserInvitation({to_user_id: user.id}),
                disableSaml: () =>
                  updateUser({id: user.id, restrict_saml_only_login: false}),
                enableSaml: () =>
                  updateUser({id: user.id, restrict_saml_only_login: true}),
              }}
            />
          </StyledMainPageHeaderDiv>

          <ModernLinkTabs
            tabs={tabs.map(({label, slug}) => ({
              label,
              to: getUserDetailPageURL(params.userid, slug),
              content: () => cloneElement(children, {key: childrenKey}),
            }))}
          />
        </Fragment>
      )}
    </SPPage>
  );
};
